import { useEffect, useMemo, useState } from "preact/hooks";
import { groupId, isSupabaseConfigured, supabase } from "./supabase";
import {
  mockDevices,
  mockEvents,
  mockIngestEvents,
  mockSession,
  mockSessionMatches,
  mockSessionMatchPlayers
} from "./mockData";
import type {
  AllPlayerSummary,
  CompanionDevice,
  IngestEvent,
  LiveEvent,
  MatchPlayerDetail,
  RoleArchetype,
  RoleWeights,
  SessionEndReason,
  SessionMatch,
  SessionMeta,
  SessionSummary
} from "./types";

type SessionRow = {
  id: string;
  group_id: string;
  started_at: string;
  ended_at: string | null;
  status: "active" | "closed";
  mvp_player_id: string | null;
  match_count: number;
  end_reason: SessionEndReason;
  regular_player_ids: string[] | null;
};

type SessionPlayerRow = {
  player_id: string;
  player_name: string;
  role: "owner" | "crew" | "guest";
  current_market_value: number;
  current_rating: number;
  current_streak: number;
  form_label: "heater" | "in_form" | "cold" | "frozen" | null;
  peak_market_value: number;
  peak_rating: number;
  total_matches: number;
  fantasy_points: number;
  market_value_delta: number;
  rating_delta: number;
  goals: number;
  assists: number;
  saves: number;
  wins: number;
  matches: number;
  is_regular: boolean | null;
  absent_streak: number | null;
  first_seen_seq: number | null;
  last_seen_seq: number | null;
  with_owner_matches: number | null;
  vs_owner_matches: number | null;
  role_archetype: RoleArchetype;
  role_weights: RoleWeights | null;
  consistency_sigma: number | null;
  synergy_with_owner: number | null;
};

type MatchPlayerDetailRow = {
  match_id: string;
  match_seq: number | null;
  started_at: string | null;
  ended_at: string | null;
  blue_score: number | null;
  orange_score: number | null;
  player_id: string;
  player_name: string;
  player_role: "owner" | "crew" | "guest";
  team: "blue" | "orange" | "unknown";
  goals: number | null;
  assists: number | null;
  saves: number | null;
  shots: number | null;
  score: number | null;
  fantasy_points: number | null;
  won: boolean | null;
};

type AllPlayerRow = {
  id: string;
  display_name: string;
  current_market_value: number;
  current_rating: number;
  current_streak: number;
  form_label: "heater" | "in_form" | "cold" | "frozen" | null;
  peak_market_value: number;
  peak_rating: number;
  total_matches: number;
};

const tierFor = (rating: number): string => {
  if (rating >= 98) return "Grand Champion";
  if (rating >= 93) return "Champion";
  if (rating >= 85) return "Diamant";
  if (rating >= 75) return "Gold";
  if (rating >= 60) return "Silber";
  if (rating >= 45) return "Bronze";
  return "Eisen";
};

// Bayesian shrinkage: pulls per-match average toward the newcomer baseline
// until the player has enough matches for their own rate to dominate.
const NEWBIE_FP = 35;
const PRIOR_WEIGHT = 3;
const effectiveFppm = (totalFp: number, matches: number) =>
  (totalFp + NEWBIE_FP * PRIOR_WEIGHT) / (Math.max(matches, 0) + PRIOR_WEIGHT);

// ----------------------------------------------------------------------------
//  FM-Score: effective_fppm × role × consistency × synergy
// ----------------------------------------------------------------------------
//  * Role multiplier: +5% if a clear archetype was inferred (vs allrounder).
//    Encourages players who play to a strength rather than nominally
//    contributing to every category.
//  * Consistency multiplier: scales with how far σ is from the league
//    "good" benchmark (σ≈10). Lower σ → +up to 8%, higher σ → -up to 8%.
//  * Synergy multiplier: maps the with-owner WR delta to ±8%. A teammate
//    who lifts the owner's WR pushes their FM-score up.
//
//  All three are bounded so a "perfect" player tops out around 1.21× their
//  raw effective FPPM — keeps the headline number anchored in reality.
const FM_SIGMA_BASELINE = 10;
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const fmRoleMult = (archetype: RoleArchetype) =>
  archetype && archetype !== "unknown" && archetype !== "allrounder" ? 1.05 : 1.0;
const fmConsistencyMult = (sigma: number | null) => {
  if (sigma === null) return 1.0;
  return clamp(1 + (FM_SIGMA_BASELINE - sigma) / 100, 0.92, 1.08);
};
const fmSynergyMult = (synergy: number | null, role: "owner" | "crew" | "guest") => {
  // The owner's own synergy is meaningless (always self-team) — neutralise.
  if (role === "owner" || synergy === null) return 1.0;
  return clamp(1 + synergy * 0.3, 0.92, 1.08);
};

const trendFor = (delta: number): "hot" | "stable" | "cold" => {
  if (delta > 150000) return "hot";
  if (delta < -150000) return "cold";
  return "stable";
};

const mapSession = (
  session: SessionRow,
  players: SessionPlayerRow[]
): SessionSummary => ({
  session_id: session.id,
  group_id: session.group_id,
  started_at: session.started_at,
  ended_at: session.ended_at,
  status: session.status,
  mvp_player_id: session.mvp_player_id,
  match_count: session.match_count,
  regular_player_ids: session.regular_player_ids ?? [],
  end_reason: session.end_reason ?? null,
  // Crew-only leaderboard: matchmaking opponents (role = 'guest') stay in the
  // DB so we can still compute Underdog factors and head-to-head context, but
  // they don't clutter the dashboard.
  players: players
    .filter((p) => p.role !== "guest")
    .map((player) => {
      const matches = Math.max(Number(player.matches ?? 0), 0);
      const totalFp = Number(player.fantasy_points ?? 0);
      const fppm = matches > 0 ? totalFp / matches : 0;
      const eff = Number(effectiveFppm(totalFp, matches).toFixed(2));
      const archetype: RoleArchetype = player.role_archetype ?? null;
      const sigma = player.consistency_sigma ?? null;
      const synergy = Number(player.synergy_with_owner ?? 0);
      const roleMult = fmRoleMult(archetype);
      const consistencyMult = fmConsistencyMult(sigma);
      const synergyMult = fmSynergyMult(synergy, player.role);
      const fmScore = Number((eff * roleMult * consistencyMult * synergyMult).toFixed(2));
      return {
        ...player,
        fppm: Number(fppm.toFixed(2)),
        effective_fppm: eff,
        trend: trendFor(player.market_value_delta),
        tier: tierFor(Number(player.current_rating ?? 0)),
        is_regular: Boolean(player.is_regular),
        absent_streak: Number(player.absent_streak ?? 0),
        first_seen_seq: player.first_seen_seq,
        last_seen_seq: player.last_seen_seq,
        with_owner_matches: Number(player.with_owner_matches ?? 0),
        vs_owner_matches: Number(player.vs_owner_matches ?? 0),
        role_archetype: archetype,
        role_weights: player.role_weights ?? {},
        consistency_sigma: sigma,
        synergy_with_owner: synergy,
        fm_score: fmScore,
        fm_role_mult: Number(roleMult.toFixed(3)),
        fm_consistency_mult: Number(consistencyMult.toFixed(3)),
        fm_synergy_mult: Number(synergyMult.toFixed(3))
      };
    })
    .sort((a, b) => b.fm_score - a.fm_score)
});

// Derive per-match summaries (player rosters + owner team) from the flat
// match_player_details rows. Used by the SessionTimeline.
const mapSessionMatches = (rows: MatchPlayerDetailRow[]): SessionMatch[] => {
  const byMatch = new Map<string, SessionMatch>();
  for (const row of rows) {
    const mid = String(row.match_id);
    if (!byMatch.has(mid)) {
      byMatch.set(mid, {
        match_id: mid,
        match_seq: Number(row.match_seq ?? 0),
        started_at: row.started_at,
        blue_score: Number(row.blue_score ?? 0),
        orange_score: Number(row.orange_score ?? 0),
        owner_team: null,
        present_player_ids: []
      });
    }
    const summary = byMatch.get(mid)!;
    summary.present_player_ids.push(String(row.player_id));
    if (row.player_role === "owner" && (row.team === "blue" || row.team === "orange")) {
      summary.owner_team = row.team;
    }
  }
  return Array.from(byMatch.values()).sort((a, b) => a.match_seq - b.match_seq);
};

const mapMatchPlayerDetails = (rows: MatchPlayerDetailRow[]): MatchPlayerDetail[] =>
  rows.map((row) => ({
    match_id: String(row.match_id),
    match_seq: row.match_seq,
    started_at: row.started_at,
    ended_at: row.ended_at,
    blue_score: Number(row.blue_score ?? 0),
    orange_score: Number(row.orange_score ?? 0),
    player_id: String(row.player_id),
    player_name: row.player_name,
    player_role: row.player_role,
    team: row.team,
    goals: Number(row.goals ?? 0),
    assists: Number(row.assists ?? 0),
    saves: Number(row.saves ?? 0),
    shots: Number(row.shots ?? 0),
    score: Number(row.score ?? 0),
    fantasy_points: Number(row.fantasy_points ?? 0),
    won: row.won
  }));

const mapAllPlayer = (row: AllPlayerRow): AllPlayerSummary => ({
  player_id: row.id,
  player_name: row.display_name,
  current_market_value: Number(row.current_market_value),
  current_rating: Number(row.current_rating),
  current_streak: Number(row.current_streak ?? 0),
  form_label: row.form_label,
  peak_market_value: Number(row.peak_market_value ?? 0),
  peak_rating: Number(row.peak_rating ?? 0),
  total_matches: Number(row.total_matches ?? 0),
  tier: tierFor(Number(row.current_rating ?? 0))
});

const emptySession = (gid: string): SessionSummary => ({
  session_id: "empty",
  group_id: gid,
  started_at: new Date().toISOString(),
  ended_at: null,
  status: "active",
  mvp_player_id: null,
  match_count: 0,
  regular_player_ids: [],
  end_reason: null,
  players: []
});

export const useFantasyData = () => {
  const [session, setSession] = useState<SessionSummary>(mockSession);
  const [sessions, setSessions] = useState<SessionMeta[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [allPlayers, setAllPlayers] = useState<AllPlayerSummary[]>([]);
  const [activeSessionPlayerIds, setActiveSessionPlayerIds] = useState<Set<string>>(new Set());
  // currentLobbyPlayerIds = player_ids from the LATEST match of the selected
  // session. Used to mark who is actually still in the lobby vs who has left.
  const [currentLobbyPlayerIds, setCurrentLobbyPlayerIds] = useState<Set<string>>(new Set());
  // Per-session detail rows for the SessionTimeline + OpponentsBlock.
  const [sessionMatches, setSessionMatches] = useState<SessionMatch[]>(mockSessionMatches);
  const [sessionMatchPlayers, setSessionMatchPlayers] = useState<MatchPlayerDetail[]>(
    mockSessionMatchPlayers
  );
  const [events, setEvents] = useState<LiveEvent[]>(mockEvents);
  const [devices, setDevices] = useState<CompanionDevice[]>(mockDevices);
  const [ingestEvents, setIngestEvents] = useState<IngestEvent[]>(mockIngestEvents);
  const [loading, setLoading] = useState(isSupabaseConfigured);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!supabase || !groupId) return;
    const client = supabase;
    const gid = groupId;

    let ignore = false;
    let currentSelected: string | null = null;

    const SESSION_SELECT =
      "id, group_id, started_at, ended_at, status, mvp_player_id, match_count, end_reason, regular_player_ids";

    const loadSession = async (id: string | null) => {
      let target: SessionRow | null = null;
      if (id) {
        const { data, error: e } = await client
          .from("sessions")
          .select(SESSION_SELECT)
          .eq("id", id)
          .maybeSingle<SessionRow>();
        if (!e) target = data;
      }
      if (!target) {
        const { data, error: e } = await client
          .from("sessions")
          .select(SESSION_SELECT)
          .eq("group_id", gid)
          .order("started_at", { ascending: false })
          .limit(1)
          .maybeSingle<SessionRow>();
        if (e && !ignore) setError(e.message);
        target = data ?? null;
      }
      if (ignore) return;
      if (!target) {
        setSession(emptySession(gid));
        return;
      }
      const { data: players, error: e2 } = await client
        .from("session_player_summaries")
        .select("*")
        .eq("session_id", target.id)
        .returns<SessionPlayerRow[]>();
      if (ignore) return;
      if (e2) setError(e2.message);
      setSession(mapSession(target, players ?? []));
    };

    // Fetch per-match per-player rows for the selected session. One query,
    // ~3-12 matches × 4-6 players per session — well under a kilobyte.
    const loadSessionDetails = async (id: string | null) => {
      const targetId = id && id !== "empty" ? id : null;
      if (!targetId) {
        // Resolve to "latest session" — same logic as loadSession.
        const { data: latest } = await client
          .from("sessions")
          .select("id")
          .eq("group_id", gid)
          .order("started_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (!latest?.id) {
          if (!ignore) {
            setSessionMatches([]);
            setSessionMatchPlayers([]);
          }
          return;
        }
        await loadSessionDetails(String(latest.id));
        return;
      }
      const { data, error: e } = await client
        .from("match_player_details")
        .select(
          "match_id, match_seq, started_at, ended_at, blue_score, orange_score, player_id, player_name, player_role, team, goals, assists, saves, shots, score, fantasy_points, won"
        )
        .eq("session_id", targetId)
        .returns<MatchPlayerDetailRow[]>();
      if (ignore) return;
      if (e) {
        setError(e.message);
        return;
      }
      const rows = data ?? [];
      setSessionMatchPlayers(mapMatchPlayerDetails(rows));
      setSessionMatches(mapSessionMatches(rows));
    };

    const loadSessionsList = async () => {
      const { data, error: e } = await client
        .from("sessions")
        .select("id, started_at, ended_at, status, mvp_player_id, match_count, end_reason")
        .eq("group_id", gid)
        .order("started_at", { ascending: false })
        .limit(10);
      if (ignore) return;
      if (e) {
        setError(e.message);
        return;
      }
      const rows = data ?? [];
      const mvpIds = rows.map((s) => s.mvp_player_id).filter((id): id is string => Boolean(id));
      const nameById = new Map<string, string>();
      if (mvpIds.length > 0) {
        const { data: names } = await client
          .from("players")
          .select("id, display_name")
          .in("id", mvpIds);
        for (const n of names ?? []) nameById.set(String(n.id), String(n.display_name));
      }
      if (ignore) return;
      setSessions(
        rows.map((s) => ({
          id: s.id as string,
          started_at: s.started_at as string,
          ended_at: s.ended_at as string | null,
          status: s.status as "active" | "closed",
          mvp_player_id: (s.mvp_player_id as string | null) ?? null,
          mvp_name: s.mvp_player_id ? nameById.get(String(s.mvp_player_id)) ?? null : null,
          match_count: Number(s.match_count ?? 0),
          end_reason: (s.end_reason as SessionEndReason) ?? null
        }))
      );

      // Track which players are in the currently active session — the
      // "Live" highlight in the leaderboard pulses on this set regardless
      // of which tab is open.
      const active = rows.find((s) => s.status === "active");
      if (active) {
        const { data: ap } = await client
          .from("session_player_summaries")
          .select("player_id")
          .eq("session_id", active.id);
        if (!ignore) {
          const ids = new Set<string>();
          for (const r of ap ?? []) ids.add(String(r.player_id));
          setActiveSessionPlayerIds(ids);
        }
      } else if (!ignore) {
        setActiveSessionPlayerIds(new Set());
      }
    };

    const loadCurrentLobby = async (sessionId: string | null) => {
      // Don't depend on the `sessions` React state here — it might still be
      // empty on first paint. Query the DB directly: if a session is selected,
      // pick that session's latest match; otherwise the absolute newest match
      // in the group.
      let q = client
        .from("matches")
        .select("id, session_id")
        .eq("group_id", gid)
        .order("started_at", { ascending: false })
        .limit(1);
      if (sessionId && sessionId !== "empty") q = q.eq("session_id", sessionId);
      const { data: latest } = await q.maybeSingle();
      if (ignore) return;
      if (!latest) {
        setCurrentLobbyPlayerIds(new Set());
        return;
      }
      const { data: mp } = await client
        .from("match_players")
        .select("player_id")
        .eq("match_id", latest.id);
      if (ignore) return;
      const ids = new Set<string>();
      for (const row of mp ?? []) ids.add(String(row.player_id));
      setCurrentLobbyPlayerIds(ids);
    };

    const loadAllPlayers = async () => {
      const { data, error: e } = await client
        .from("players")
        .select(
          "id, display_name, current_market_value, current_rating, current_streak, form_label, peak_market_value, peak_rating, total_matches"
        )
        .eq("group_id", gid)
        .order("current_market_value", { ascending: false })
        .limit(30)
        .returns<AllPlayerRow[]>();
      if (ignore) return;
      if (e) {
        setError(e.message);
        return;
      }
      setAllPlayers((data ?? []).map(mapAllPlayer));
    };

    const loadDevices = async () => {
      const { data, error: e } = await client
        .from("companion_devices")
        .select("id, device_id, device_name, last_seen_at")
        .eq("group_id", gid)
        .order("last_seen_at", { ascending: false })
        .limit(8)
        .returns<CompanionDevice[]>();
      if (ignore) return;
      if (e) setError(e.message);
      else setDevices(data ?? []);
    };

    const loadIngestEvents = async () => {
      const { data, error: e } = await client
        .from("ingest_events")
        .select("id, device_id, event_type, raw_event_type, external_match_id, players_count, accepted, error, observed_at")
        .eq("group_id", gid)
        .order("observed_at", { ascending: false })
        .limit(8)
        .returns<IngestEvent[]>();
      if (ignore) return;
      if (e) setError(e.message);
      else setIngestEvents(data ?? []);
    };

    const refreshAll = async () => {
      setLoading(true);
      await Promise.all([
        loadSessionsList(),
        loadSession(currentSelected),
        loadSessionDetails(currentSelected),
        loadCurrentLobby(currentSelected),
        loadAllPlayers(),
        loadDevices(),
        loadIngestEvents()
      ]);
      if (!ignore) setLoading(false);
    };

    void refreshAll();

    const channel = client
      .channel(`group:${gid}:fantasy`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "sessions", filter: `group_id=eq.${gid}` },
        () => {
          void loadSessionsList();
          void loadSession(currentSelected);
          void loadSessionDetails(currentSelected);
          void loadCurrentLobby(currentSelected);
        }
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "matches", filter: `group_id=eq.${gid}` },
        () => {
          void loadCurrentLobby(currentSelected);
          void loadSessionDetails(currentSelected);
        }
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "players", filter: `group_id=eq.${gid}` },
        () => void loadAllPlayers()
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "companion_devices", filter: `group_id=eq.${gid}` },
        () => void loadDevices()
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "ingest_events", filter: `group_id=eq.${gid}` },
        (payload) => {
          const row = payload.new as IngestEvent;
          setIngestEvents((cur) => [row, ...cur].slice(0, 8));
        }
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "live_events", filter: `group_id=eq.${gid}` },
        (payload) => {
          const row = payload.new as LiveEvent;
          setEvents((cur) => [row, ...cur].slice(0, 8));
        }
      )
      .subscribe();

    // Two intervals, two jobs:
    //  - fast (250ms): notice when the user picks a different session and
    //    re-fetch its leaderboard. Cheap, just compares state to closure var.
    //  - slow (15s): polling fallback in case Realtime drops a websocket or
    //    a publication is misconfigured. Re-fetches the visible state.
    const fast = setInterval(() => {
      if (currentSelected !== selectedSessionId) {
        currentSelected = selectedSessionId;
        void loadSession(currentSelected);
        void loadSessionDetails(currentSelected);
        void loadCurrentLobby(currentSelected);
      }
    }, 250);
    const slow = setInterval(() => {
      void loadSessionsList();
      void loadSession(currentSelected);
      void loadSessionDetails(currentSelected);
      void loadCurrentLobby(currentSelected);
      void loadDevices();
      void loadIngestEvents();
    }, 15000);

    return () => {
      ignore = true;
      clearInterval(fast);
      clearInterval(slow);
      void client.removeChannel(channel);
    };
  }, [selectedSessionId]);

  const mvp = useMemo(
    () => session.players.find((player) => player.player_id === session.mvp_player_id) ?? session.players[0],
    [session]
  );

  const activeDevices = useMemo(
    () => devices.filter((device) => Date.now() - Date.parse(device.last_seen_at) < 60 * 1000),
    [devices]
  );

  return {
    session,
    sessions,
    sessionMatches,
    sessionMatchPlayers,
    selectedSessionId,
    selectSession: setSelectedSessionId,
    allPlayers,
    activeSessionPlayerIds,
    currentLobbyPlayerIds,
    events,
    devices,
    ingestEvents,
    activeDevices,
    mvp,
    loading,
    error,
    isMock: !isSupabaseConfigured
  };
};
